import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import SvgUpload from "@/assets/icons/SVGUpload";
import SvgCopyText from "@/assets/icons/SVGCopyText";
import SvgSimpleCheckMark from "@/assets/icons/SVGSimpleCheckMark";
import {
  getModalContent,
  buildProjectDetailsSection,
  buildReviewResultSection,
  createMinimalProjectDetails,
  getBatteryConfiguration,
} from "../utils/sizingToolUtils";
import { SizingToolResultModalProps, ProjectDetails } from "../types";
import { uploadProjectFileReq } from "@/api/projectsApi";
import { useProjectsStore } from "@/store/projectsStore";
import { useFileValidation } from "@/hooks/useFileValidation";
import { formatShortMonthDate } from "@/utils/formatDate";

export const SizingToolResultModal: React.FC<SizingToolResultModalProps> = ({
  type,
  sizingResult,
  sizingError,
  onBack,
  onAnalyze,
  file,
  uploading,
  handleFileChange,
  handleCalculationFailureUpload,
  accountHash,
  customerName,
  projectId,
  projectDetails,
  sizingFormData,
  hideUploadAndNextSteps = false,
  onClose,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [displayFile, setDisplayFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [copiedSuccessfully, setCopiedSuccessfully] = useState(false);
  const { validateFile, validationError: fileValidationError } =
    useFileValidation();
  const getProjectFiles = useProjectsStore((state) => state.getProjectFiles);

  const resolvedProjectId = projectId;

  const formattedAnalysisDate = useMemo(
    () => formatShortMonthDate(new Date()),
    []
  );

  const batteryConfiguration = useMemo(
    () => getBatteryConfiguration(projectDetails, sizingFormData),
    [
      projectDetails?.batteryOem,
      projectDetails?.batteryModel,
      sizingFormData?.batteryManufacturer,
      sizingFormData?.batteryProductName,
    ]
  );

  useEffect(() => {
    setDisplayFile(file);
  }, [file]);

  useEffect(() => {
    if (!file && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [file]);

  const handleCalculationFailureUploadClick = useCallback(async () => {
    if (!displayFile) {
      return;
    }

    if (!resolvedProjectId) {
      return;
    }

    setIsUploading(true);

    try {
      if (handleCalculationFailureUpload) {
        await handleCalculationFailureUpload(displayFile);
      } else {
        const formData = new FormData();
        formData.append("file", displayFile);
        formData.append("type", "System Sizing Tool");

        await uploadProjectFileReq(Number(resolvedProjectId), formData);
        getProjectFiles(Number(resolvedProjectId), 1, 10, "", [], null);
      }

      onClose?.();
    } catch (error) {
      console.error("Failed to upload file:", error);
      throw new Error("Failed to upload file. Please try again.");
    } finally {
      setIsUploading(false);
    }
  }, [
    displayFile,
    resolvedProjectId,
    handleCalculationFailureUpload,
    getProjectFiles,
    onClose,
  ]);

  const handleFileChangeWithDisplay = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];

      if (selectedFile) {
        const { isValid } = validateFile(selectedFile);

        if (isValid) {
          setDisplayFile(selectedFile);
        } else {
          setDisplayFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      }
      handleFileChange(e);
    },
    [handleFileChange, validateFile]
  );

  const content = getModalContent(
    type,
    sizingResult,
    sizingError,
    projectDetails || createMinimalProjectDetails(accountHash, customerName)
  );

  const handleCopyAllData = useCallback(() => {
    // Use projectDetails if available, otherwise create minimal project details
    const effectiveProjectDetails =
      projectDetails ||
      (createMinimalProjectDetails(
        accountHash,
        customerName
      ) as ProjectDetails | null);

    const sections = [
      ...buildProjectDetailsSection(
        effectiveProjectDetails,
        sizingResult,
        sizingFormData
      ),
      ...buildReviewResultSection(content),
      // ...buildMonthlySection(sizingResult), // intentionally disabled (monthly table hidden)
    ];

    const textToCopy = sections.join("\n");

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setCopiedSuccessfully(true);
        setTimeout(() => setCopiedSuccessfully(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy data: ", err);
      });
  }, [
    projectDetails,
    sizingResult,
    content,
    accountHash,
    customerName,
    sizingFormData,
  ]);

  return (
    <Box sx={{ py: 0, overflow: "visible", width: "100%" }}>
      {(type === "success" ||
        type === "insufficient" ||
        sizingResult?.isSufficient !== undefined) && (
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              fontWeight: 600,
              mb: 1.5,
              color: theme.palette.text.primary,
              fontSize: "15px",
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            {content.title}
          </Typography>
          <Box sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 1,
                  mb: 1,
                  px: 1,
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: theme.palette.grey[600],
                      mb: 0.25,
                      fontWeight: 500,
                    }}
                  >
                    Project ID
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    {projectDetails?.id?.toString()?.padStart(4, "0") || "N/A"}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: theme.palette.grey[600],
                      mb: 0.25,
                      fontWeight: 500,
                    }}
                  >
                    Project Name
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    {projectDetails != null
                      ? (projectDetails.customerName || "N/A")
                      : (sizingFormData?.customerName || "N/A")}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: theme.palette.grey[600],
                      mb: 0.25,
                      fontWeight: 500,
                    }}
                  >
                    Battery Configuration
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    {batteryConfiguration}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: theme.palette.grey[600],
                      mb: 0.25,
                      fontWeight: 500,
                    }}
                  >
                    Battery Quantity
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    {projectDetails != null
                      ? (projectDetails.numberOfBatteries || "N/A")
                      : (sizingFormData?.batteryQuantity || "N/A")}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 1,
                  px: 1,
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: theme.palette.grey[600],
                      mb: 0.25,
                      fontWeight: 500,
                    }}
                  >
                    Existing Solar kW-DC
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    {projectDetails != null
                      ? (projectDetails.solarKwdc ?? "N/A")
                      : (sizingFormData?.existingSolarKwdc || "N/A")}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: theme.palette.grey[600],
                      mb: 0.25,
                      fontWeight: 500,
                    }}
                  >
                    New Solar kW-DC
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    {projectDetails != null
                      ? (projectDetails.newSolarKwdc ?? "N/A")
                      : (sizingFormData?.newSolarKwdc || "N/A")}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: theme.palette.grey[600],
                      mb: 0.25,
                      fontWeight: 500,
                    }}
                  >
                    Analysis Date
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    {formattedAnalysisDate}
                  </Typography>
                </Box>
              </Box>
            </Box>

          <Box
            sx={{
              background: content.isSuccess
                ? theme.palette.sizingTool.success.background
                : theme.palette.sizingTool.warning.background,
              border: "none",
              borderRadius: "24px",
              p: "16px 24px",
              mb: 1.5,
              position: "relative",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "24px",
                mb: content.isSuccess ? "12px" : "8px",
                color: content.isSuccess
                  ? theme.palette.sizingTool.success.text
                  : theme.palette.sizingTool.warning.text,
                lineHeight: 1.2,
                textAlign: "center",
              }}
            >
              {content.mainMessage}
            </Typography>
            {content.description && (
              <Typography
                sx={{
                  fontSize: content.isSuccess ? "13px" : "15px",
                  color: content.isSuccess
                    ? theme.palette.darkBlue
                    : theme.palette.sizingTool.warning.text,
                  fontWeight: content.isSuccess ? 500 : 600,
                  lineHeight: 1.2,
                  textAlign: "center",
                }}
              >
                {content.description}
              </Typography>
            )}
            {content.subDescription && (
              <Typography
                sx={{
                  fontSize: "13px",
                  color: theme.palette.darkBlue,
                  fontWeight: 400,
                  lineHeight: 1.2,
                  textAlign: "center",
                  mt: 0.75,
                }}
              >
                {content.subDescription}
              </Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
              <Button
                onClick={handleCopyAllData}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: "8px",
                  backgroundColor: "white",
                  border: `1px solid ${theme.palette.divider}`,
                  color: theme.palette.darkBlue,
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: theme.palette.grey[50],
                  },
                }}
              >
                {copiedSuccessfully ? (
                  <SvgSimpleCheckMark
                    style={{
                      width: "18px",
                      height: "18px",
                      color: theme.palette.success?.main || "#4CAF50",
                    }}
                  />
                ) : (
                  <SvgCopyText />
                )}
                {copiedSuccessfully ? "Copied" : "Copy"}
              </Button>
            </Box>
          </Box>
          {/* Monthly results table intentionally hidden (per request). */}
          {/* NOSONAR - keep for quick re-enable */}
          {/*
          {sizingResult?.monthlyResults && sizingResult.monthlyResults.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: "none",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "12px",
                  maxHeight: "calc(100vh - 450px)",
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: "13px",
                          color: theme.palette.text.primary,
                          py: 0.75,
                        }}
                      >
                        Month
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: "13px",
                          color: theme.palette.text.primary,
                          py: 0.75,
                        }}
                      >
                        Daily Average Excess/Deficit (kWh)
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: "13px",
                          color: theme.palette.text.primary,
                          py: 0.75,
                        }}
                      >
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((monthNumber) => {
                      const monthData = sizingResult.monthlyResults?.find(
                        (m) => m.month === monthNumber
                      );

                      let combinedValue: number | null = null;
                      if (
                        monthData?.averageDailyExcessPerMonth !== undefined &&
                        monthData.averageDailyExcessPerMonth > 0
                      ) {
                        combinedValue = monthData.averageDailyExcessPerMonth;
                      } else if (
                        monthData?.combinedExcessAndProduction !== undefined &&
                        monthData.combinedExcessAndProduction !== 0
                      ) {
                        combinedValue = monthData.combinedExcessAndProduction;
                      }

                      const isPositive = combinedValue !== null && combinedValue > 0;
                      const isPassing = monthData?.sufficient ?? false;

                      let cellColor = theme.palette.text.primary;
                      if (combinedValue !== null) {
                        cellColor = isPositive
                          ? theme.palette.success?.main || "#4CAF50"
                          : theme.palette.error?.main || "#F44336";
                      }

                      return (
                        <TableRow
                          key={monthNumber}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            "&:hover": { backgroundColor: theme.palette.grey[50] },
                          }}
                        >
                          <TableCell
                            sx={{
                              fontSize: "13px",
                              color: theme.palette.text.primary,
                              py: 0.5,
                            }}
                          >
                            {getMonthName(monthNumber)}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "13px",
                              color: cellColor,
                              py: 0.5,
                              fontWeight: 500,
                            }}
                          >
                            {combinedValue === null ? "N/A" : combinedValue.toFixed(1)}
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: "50px",
                                px: 1.5,
                                py: 0.25,
                                borderRadius: "4px",
                                backgroundColor: isPassing
                                  ? theme.palette.sizingTool.success.background
                                  : theme.palette.sizingTool.error.background,
                                color: isPassing
                                  ? theme.palette.sizingTool.success.text
                                  : theme.palette.sizingTool.error.text,
                                fontSize: "11px",
                                fontWeight: 600,
                              }}
                            >
                              {isPassing ? "Pass" : "Fail"}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          */}
        </Box>
      )}

      {type === "genericError" && (
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              background: theme.palette.sizingTool.error.background,
              border: "none",
              borderRadius: "24px",
              p: "22px 32px",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                mb: "16px",
                color: theme.palette.darkBlue,
                fontSize: "19.53px",
                lineHeight: 1.215,
                textAlign: "center",
              }}
            >
              {content.title}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "24px",
                mb: "16px",
                color: theme.palette.sizingTool.error.text,
                lineHeight: 1.215,
                textAlign: "center",
              }}
            >
              {content.mainMessage}
            </Typography>
            {content.description && (
              <Typography
                sx={{
                  fontSize: "16px",
                  color: theme.palette.darkBlue,
                  fontWeight: 500,
                  lineHeight: 1.215,
                  textAlign: "center",
                }}
              >
                {content.description}
              </Typography>
            )}
          </Box>

          {content.nextSteps && content.nextSteps.length > 0 && (
            <Box
              sx={{
                background: theme.palette.sizingTool.neutral.background,
                border: `1px solid ${theme.palette.sizingTool.neutral.border}`,
                borderRadius: "12px",
                p: "16px",
                mb: 2,
                textAlign: "left",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: theme.palette.sizingTool.neutral.text,
                  mb: "12px",
                }}
              >
                Next Steps:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {content.nextSteps.map((step, index) => (
                  <Typography
                    key={index}
                    component="li"
                    sx={{
                      fontSize: "14px",
                      color: theme.palette.sizingTool.neutral.text,
                      mb: "8px",
                      lineHeight: 1.4,
                    }}
                  >
                    {step}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
          {content.infoText && (
            <Box
              sx={{
                background: theme.palette.sizingTool.info.background,
                border: `1px solid ${theme.palette.sizingTool.info.border}`,
                borderRadius: "8px",
                p: "12px",
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: theme.palette.sizingTool.info.text,
                  lineHeight: 1.4,
                }}
              >
                {content.infoText}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {type === "accountNotFound" && (
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              background: theme.palette.sizingTool.error.background,
              border: "none",
              borderRadius: "24px",
              p: "22px 32px",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                mb: "16px",
                color: theme.palette.darkBlue,
                fontSize: "19.53px",
                lineHeight: 1.215,
                textAlign: "center",
              }}
            >
              {content.title}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "24px",
                mb: "16px",
                color: theme.palette.sizingTool.error.text,
                lineHeight: 1.215,
                textAlign: "center",
              }}
            >
              {content.mainMessage}
            </Typography>
            {content.description && (
              <Typography
                sx={{
                  fontSize: "16px",
                  color: theme.palette.darkBlue,
                  fontWeight: 500,
                  lineHeight: 1.215,
                  textAlign: "center",
                }}
              >
                {content.description}
              </Typography>
            )}
          </Box>
          {content.nextSteps && content.nextSteps.length > 0 && (
            <Box
              sx={{
                background: theme.palette.sizingTool.neutral.background,
                border: `1px solid ${theme.palette.sizingTool.neutral.border}`,
                borderRadius: "12px",
                p: "16px",
                mb: 2,
                textAlign: "left",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: theme.palette.sizingTool.neutral.text,
                  mb: "12px",
                }}
              >
                Next Steps:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {content.nextSteps.map((step, index) => (
                  <Typography
                    key={index}
                    component="li"
                    sx={{
                      fontSize: "14px",
                      color: theme.palette.sizingTool.neutral.text,
                      mb: "8px",
                      lineHeight: 1.4,
                    }}
                  >
                    {step}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
          {content.infoText && (
            <Box
              sx={{
                background: theme.palette.sizingTool.info.background,
                border: `1px solid ${theme.palette.sizingTool.info.border}`,
                borderRadius: "8px",
                p: "12px",
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: theme.palette.sizingTool.info.text,
                  lineHeight: 1.4,
                }}
              >
                {content.infoText}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {type === "insufficientData" && (
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              background: theme.palette.sizingTool.error.background,
              border: "none",
              borderRadius: "24px",
              p: "22px 32px",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                mb: "16px",
                color: theme.palette.darkBlue,
                fontSize: "19.53px",
                lineHeight: 1.215,
                textAlign: "center",
              }}
            >
              {content.title}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "24px",
                mb: "16px",
                color: theme.palette.sizingTool.error.text,
                lineHeight: 1.215,
                textAlign: "center",
              }}
            >
              {content.mainMessage}
            </Typography>
            {content.description && (
              <Typography
                sx={{
                  fontSize: "16px",
                  color: theme.palette.darkBlue,
                  fontWeight: 500,
                  lineHeight: 1.215,
                  textAlign: "center",
                }}
              >
                {content.description}
              </Typography>
            )}
          </Box>
          {content.nextSteps && content.nextSteps.length > 0 && (
            <Box
              sx={{
                background: theme.palette.sizingTool.neutral.background,
                border: `1px solid ${theme.palette.sizingTool.neutral.border}`,
                borderRadius: "12px",
                p: "16px",
                mb: 2,
                textAlign: "left",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: theme.palette.sizingTool.neutral.text,
                  mb: "12px",
                }}
              >
                Next Steps:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {content.nextSteps.map((step, index) => (
                  <Typography
                    key={index}
                    component="li"
                    sx={{
                      fontSize: "14px",
                      color: theme.palette.sizingTool.neutral.text,
                      mb: "8px",
                      lineHeight: 1.4,
                    }}
                  >
                    {step}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
          {content.infoText && (
            <Box
              sx={{
                background: theme.palette.sizingTool.info.background,
                border: `1px solid ${theme.palette.sizingTool.info.border}`,
                borderRadius: "8px",
                p: "12px",
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: theme.palette.sizingTool.info.text,
                  lineHeight: 1.4,
                }}
              >
                {content.infoText}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {(type === "calculationFailure" || sizingResult?.calculationFailed) && (
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              background: "transparent",
              border: "none",
              borderRadius: "24px",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                mb: "16px",
                color: theme.palette.darkBlue,
                fontSize: "16px",
                lineHeight: 1.3,
                textAlign: "left",
              }}
            >
              {content.mainMessage}
            </Typography>

            <Typography
              sx={{
                fontSize: "16px",
                color: theme.palette.sizingTool.neutral.textSecondary,
                fontWeight: 400,
                lineHeight: 1.4,
                textAlign: "left",
                mb: "24px",
              }}
            >
              {content.description}
            </Typography>

            {content.nextSteps && content.nextSteps.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: theme.palette.darkBlue,
                    mb: "12px",
                    textAlign: "left",
                  }}
                >
                  Next Steps:
                </Typography>

                {content.nextSteps.map((step, index) => (
                  <Typography
                    key={index}
                    sx={{
                      fontSize: "14px",
                      color: theme.palette.darkBlue,
                      lineHeight: 1.4,
                      textAlign: "left",
                      mb: "16px",
                    }}
                  >
                    {step.includes("Green Button data from SDG&E") ? (
                      <>
                        If the customer can provide at least 6 months of{" "}
                        <a
                          href="https://www.sdge.com/green-button"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: theme.palette.sizingTool.primary.main,
                            textDecoration: "underline",
                            fontWeight: 600,
                          }}
                        >
                          Green Button data from SDG&E
                        </a>{" "}
                        for this service address, please complete and upload the
                        Excel-based System Sizing Tool (located in the Templates
                        folder).
                      </>
                    ) : (
                      step
                    )}
                  </Typography>
                ))}
              </Box>
            )}

            {!hideUploadAndNextSteps && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: theme.palette.darkBlue,
                    fontSize: "14px",
                    textAlign: "left",
                  }}
                >
                  System Sizing Tool Output{" "}
                  <span style={{ color: theme.palette.sizingTool.required }}>
                    *
                  </span>
                </Typography>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.border.light}`,
                    borderRadius: "8px",
                    background: theme.palette.customBackground.light,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    cursor: "pointer",
                    "&:hover": {
                      background:
                        theme.palette.sizingTool.neutral.backgroundHover,
                    },
                  }}
                  component="label"
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: theme.palette.sizingTool.neutral.textSecondary,
                    }}
                  >
                    <SvgUpload />
                  </Box>
                  <Box sx={{ flex: 1, marginLeft: 1.5 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: displayFile
                          ? theme.palette.darkBlue
                          : theme.palette.sizingTool.neutral.textSecondary,
                        fontWeight: displayFile ? 600 : 400,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textAlign: "left",
                      }}
                    >
                      {displayFile ? displayFile.name : "Upload Excel file"}
                    </Typography>
                    {displayFile && (
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: theme.palette.sizingTool.neutral.textSecondary,
                          fontWeight: 400,
                          textAlign: "left",
                          mt: 0.25,
                        }}
                      >
                        {(displayFile.size / 1024).toFixed(2)} KB
                      </Typography>
                    )}
                  </Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileChangeWithDisplay}
                    style={{ display: "none" }}
                  />
                </Box>
                {fileValidationError && (
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: theme.palette.sizingTool.error.text,
                      fontWeight: 500,
                      mt: 1,
                      textAlign: "left",
                    }}
                  >
                    {fileValidationError}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
      {(type === "calculationFailure" || sizingResult?.calculationFailed) && (
        <>
          <Box
            sx={{
              width: "calc(100% + 48px)",
              height: "1px",
              backgroundColor: theme.palette.divider,
              mt: 4,
              mb: 3,
              marginLeft: "-24px",
              marginRight: "-24px",
            }}
          />
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="outlined"
              onClick={() => {
                if (onClose) onClose();
              }}
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "16px",
                textTransform: "none",
                border: `2px solid ${theme.palette.sizingTool.primary.main}`,
                color: theme.palette.sizingTool.primary.main,
                backgroundColor: "transparent",
                "&:hover": {
                  border: `2px solid ${theme.palette.sizingTool.primary.hover}`,
                  backgroundColor: "rgba(0, 118, 206, 0.04)",
                },
              }}
            >
              Cancel
            </Button>

            {!hideUploadAndNextSteps && (
              <Button
                variant="contained"
                disabled={!displayFile || uploading || isUploading}
                onClick={() => {
                  handleCalculationFailureUploadClick();
                }}
                sx={{
                  flex: 1,
                  py: 1.5,
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "16px",
                  textTransform: "none",
                  backgroundColor:
                    displayFile && !uploading
                      ? theme.palette.sizingTool.primary.main
                      : theme.palette.customBackground.disabled,
                  border: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor:
                      displayFile && !uploading
                        ? theme.palette.sizingTool.primary.hover
                        : theme.palette.customBackground.disabled,
                    boxShadow: "none",
                  },
                  "&:disabled": {
                    backgroundColor: theme.palette.customBackground.disabled,
                    color: theme.palette.sizingTool.neutral.textSecondary,
                  },
                }}
              >
                {uploading || isUploading ? "Uploading..." : "Upload"}
              </Button>
            )}
          </Box>
        </>
      )}

      {(type === "genericError" ||
        type === "insufficientData" ||
        type === "accountNotFound") && (
        <Box sx={{ mt: 4 }}>
          {!hideUploadAndNextSteps && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: theme.palette.sizingTool.neutral.text,
                    mb: "12px",
                  }}
                >
                  Next Steps:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: theme.palette.sizingTool.neutral.text,
                    lineHeight: 1.4,
                  }}
                >
                  If the customer can provide at least 6 months of{" "}
                  <a
                    href="https://www.sdge.com/green-button"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: theme.palette.sizingTool.primary.main,
                      textDecoration: "underline",
                    }}
                  >
                    Green Button data from SDG&E
                  </a>{" "}
                  for this service address, please complete and upload the{" "}
                  <span
                    style={{
                      color: theme.palette.sizingTool.primary.main,
                      textDecoration: "underline",
                    }}
                  >
                    System Sizing Tool
                  </span>
                  .
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: theme.palette.textGray1,
                    fontSize: "14px",
                  }}
                >
                  System Sizing Tool Output{" "}
                  <span style={{ color: theme.palette.sizingTool.required }}>
                    *
                  </span>
                </Typography>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.border.light}`,
                    borderRadius: "8px",
                    background: theme.palette.customBackground.light,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    cursor: "pointer",
                    "&:hover": {
                      background:
                        theme.palette.sizingTool.neutral.backgroundHover,
                    },
                  }}
                  component="label"
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: theme.palette.sizingTool.primary.main,
                    }}
                  >
                    <SvgUpload />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: displayFile
                          ? theme.palette.darkBlue
                          : theme.palette.sizingTool.neutral.textSecondary,
                        fontWeight: displayFile ? 600 : 400,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {displayFile ? displayFile.name : "Upload Excel file"}
                    </Typography>
                    {displayFile && (
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: theme.palette.sizingTool.neutral.textSecondary,
                          fontWeight: 400,
                          mt: 0.25,
                        }}
                      >
                        {(displayFile.size / 1024).toFixed(2)} KB
                      </Typography>
                    )}
                  </Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileChangeWithDisplay}
                    style={{ display: "none" }}
                  />
                </Box>
                {fileValidationError && (
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: theme.palette.sizingTool.error.text,
                      fontWeight: 500,
                      mt: 1,
                      textAlign: "left",
                    }}
                  >
                    {fileValidationError}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: theme.palette.sizingTool.neutral.text,
                    lineHeight: 1.4,
                    fontStyle: "italic",
                  }}
                >
                  If not, apply a Solar-to-Battery ratio limit of 1:2.5, where
                  the battery's usable capacity (kWh) must not exceed 2.5 times
                  the solar system's AC power rating (kW-AC).
                </Typography>
              </Box>
            </>
          )}
          <Box
            sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center" }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                if (onBack) onBack();
              }}
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "16px",
                textTransform: "none",
                border: `2px solid ${theme.palette.sizingTool.primary.main}`,
                color: theme.palette.sizingTool.primary.main,
                backgroundColor: "transparent",
                "&:hover": {
                  border: `2px solid ${theme.palette.sizingTool.primary.hover}`,
                  backgroundColor: "rgba(0, 118, 206, 0.04)",
                },
              }}
            >
              Cancel
            </Button>

            {displayFile && (
              <Button
                variant="contained"
                onClick={() => {
                  if (onAnalyze) onAnalyze();
                }}
                sx={{
                  flex: 1,
                  py: 1.5,
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "16px",
                  textTransform: "none",
                  backgroundColor: theme.palette.sizingTool.primary.main,
                  border: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: theme.palette.sizingTool.primary.hover,
                    boxShadow: "none",
                  },
                }}
              >
                Upload
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
